import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable } from "rxjs";
import { distinctUntilChanged, map } from "rxjs/operators";
import { BackOfficeStorageService } from "./storage.service";
import {
  BackOfficeHierarchySelection,
  hierarchyHelpers,
} from "../utils/hierarchy-helpers";

export type BackOfficeHierarchyScope = "lane" | "store" | "none";

@Injectable({ providedIn: "root" })
export class BackOfficeHierarchyContextService {
  private readonly storageKey = "global.hierarchySelection";
  private readonly selectionSubject: BehaviorSubject<BackOfficeHierarchySelection>;

  readonly selection$: Observable<BackOfficeHierarchySelection>;

  constructor(private storageService: BackOfficeStorageService) {
    const storedSelection = this.storageService.get<BackOfficeHierarchySelection>(
      this.storageKey,
      {},
    );
    const initialSelection = hierarchyHelpers.normalizeHierarchySelection(
      storedSelection,
    );

    this.selectionSubject = new BehaviorSubject(initialSelection);
    this.selection$ = this.selectionSubject.asObservable().pipe(
      distinctUntilChanged((previous, current) =>
        this.isSameSelection(previous, current),
      ),
    );
  }

  get selection(): BackOfficeHierarchySelection {
    return this.selectionSubject.value;
  }

  setSelection(selection: BackOfficeHierarchySelection): void {
    const normalizedSelection = hierarchyHelpers.normalizeHierarchySelection(
      selection,
    );

    if (this.isSameSelection(this.selection, normalizedSelection)) {
      return;
    }

    this.storageService.set(this.storageKey, normalizedSelection);
    this.selectionSubject.next(normalizedSelection);
  }

  selectionForScope$(
    scope: BackOfficeHierarchyScope,
  ): Observable<BackOfficeHierarchySelection> {
    return this.selection$.pipe(
      map((selection) => this.selectionForScope(selection, scope)),
      distinctUntilChanged((previous, current) =>
        this.isSameSelection(previous, current),
      ),
    );
  }

  selectionForScope(
    selection: BackOfficeHierarchySelection,
    scope: BackOfficeHierarchyScope,
  ): BackOfficeHierarchySelection {
    if (scope === "none") {
      return { bannerId: null, storeId: null, laneId: null };
    }

    return hierarchyHelpers.normalizeHierarchySelection(selection, {
      includeLane: scope === "lane",
    });
  }

  private isSameSelection(
    left: BackOfficeHierarchySelection,
    right: BackOfficeHierarchySelection,
  ): boolean {
    return (
      left.bannerId === right.bannerId &&
      left.storeId === right.storeId &&
      left.laneId === right.laneId
    );
  }
}
